/*
 * Copyright 2024 The Kokabo Authors. All rights reserved.
 * Use of this source code is governed by a BSD-style
 * license that can be found in the LICENSE file.
 */

import {HubInfo} from "./HubInfo.js";
import {SecurityAgent} from "../e2ee/SecurityAgent";
import {Utils} from "../utils/Utils";
/**
 * REST helper for hub-side actor operations.
 *
 * This is intentionally narrow: protocol messaging goes through Actor/ExecutionMessage,
 * while lifecycle and administrative operations use REST.
 */
export class RemoteApi {

    static SharedRegistryName = "shared";

    /**
     * @param {string} actorUUID
     * @param {HubInfo} hubInfos
     */
    constructor(actorUUID, hubInfos) {
        if (!actorUUID||actorUUID ===""){
            throw new Error("actorUUID is required for identification");
        }
        if (!hubInfos instanceof HubInfo){
            throw new Error("hubInfos must be a valid HubInfo instance");
        }
        this.actorUUID = actorUUID;
        this.hubName = hubInfos.name;
        this.hubCode = hubInfos.code;
        this.host = hubInfos.host;
        this.aesKey = hubInfos.aesKey
        this.apiHost = SecurityAgent.apiHostFrom(this.host);
    }

    /////////////////////////////
    ///  Remote Actor          //
    /////////////////////////////

    /**
     * @param {string} registryName
     * @param {string} actorName
     * @param {string} e2eeCode
     * @param {number} firstUsageTimeout in seconds 60 seconds per default
     * @returns {Promise<Object>}
     */
    async createRemoteActor(registryName, actorName = Utils.v4UUID(), e2eeCode = Utils.v4UUID(), firstUsageTimeout = 60) {
        const endpoint = `${this.apiHost}/api/v1/actors/create/local/actor/in/${this.hubName}`;
        const headers = this._httpHeaders();
        const creationData = {
            registryName: registryName,
            claimedName: actorName,
            e2eeCode: e2eeCode,
            firstUsageTimeout: firstUsageTimeout
        };
        const response = await SecurityAgent.fetch(this.actorUUID, this.aesKey, endpoint, {
            method: 'POST',
            headers: headers,
            body: JSON.stringify(creationData)
        });
        return await response.json();
    }

    /**
     * killRemoteActor
     * @param actorName
     * @param e2eeCode
     * @return {Promise<any>}
     */
    async killRemoteActor(actorName, e2eeCode){
        const endpoint = `${this.apiHost}/api/v1/actors/create/local/actor/in/${this.hubName}`;
        const headers = this._httpHeaders();
        const response = await SecurityAgent.fetch(this.actorUUID, this.aesKey, endpoint, {
            method: 'DELETE',
            headers: headers,
        });
        return await response.json();
    }




    /**
     * Ensures JSON defaults for hub REST calls while allowing callers to override.
     * @param headers {Headers|undefined|Object.<string, string>}
     * @returns Headers
     * @private
     */
    _httpHeaders(headers = undefined) {
        if (headers === undefined) {
            headers = new Headers({});
        }
        const httpHeaders = headers instanceof Headers ? headers : new Headers(headers);
        if (!'Content-Type' in headers) {
            httpHeaders.set("Content-Type", "application/json");
        }
        return httpHeaders;
    }
}